import { useMemo, useState } from "react";
import { diatonicChords, voiceDiatonicChord, type Degree } from "../../../theory/diatonic";
import { describeCadence, describeProgression, generateProgression, type Cadence, type GeneratorOptions, type Mood, type Style } from "../../../theory/generator";
import { keyName, keyTonics, type Mode, type MusicalKey } from "../../../theory/key";
import { useProject, useServices } from "../../context";
import { Panel, Select, Toggle } from "../controls";

type Item = { degree: Degree; seventh: boolean };

/** Rule-based progression generator with editable, explained results and "Send to Studio". */
export function ProgressionGenerator() {
  const { controller } = useServices();
  const projectKey = useProject((p) => p.song.key);
  const song = useProject((p) => p.song);
  const [key, setKey] = useState<MusicalKey>(projectKey);
  const [opts, setOpts] = useState<Omit<GeneratorOptions, "key" | "seed">>({
    length: 4, style: "pop", mood: "neutral", complexity: 1, startOnTonic: true, cadence: "authentic",
  });
  const [items, setItems] = useState<Item[] | null>(null);
  const [seed, setSeed] = useState<number | null>(null);
  const [inversion, setInversion] = useState(0);
  const set = <K extends keyof typeof opts>(k: K, v: (typeof opts)[K]) => setOpts((o) => ({ ...o, [k]: v }));

  const described = useMemo(() => (items ? describeProgression(key, items) : null), [items, key]);
  const cadence = useMemo(() => (items ? describeCadence(items) : null), [items]);
  const options = diatonicChords(key).map((c) => ({ value: c.degree, label: `${c.roman} — ${c.symbol}` }));

  const generate = () => {
    const s = Math.floor(Math.random() * 1_000_000);
    const r = generateProgression({ ...opts, key, seed: s });
    setSeed(s);
    setItems(r.chords.map(({ degree, seventh }) => ({ degree, seventh })));
  };
  const barSec = (60 / song.tempoBpm) * song.timeSignature.beatsPerBar;
  const play = () => items && controller.previewSequence(
    items.map((it) => voiceDiatonicChord(key, it.degree, { octave: 3, seventh: it.seventh, inversion })), Math.min(barSec, 2.5), { transpose: false },
  );
  const send = () => {
    if (!items) return;
    controller.updateProject((p) => ({
      ...p,
      song: { ...p.song, key },
      sequence: { ...p.sequence, progression: { ...p.sequence.progression, chords: items.map((i) => ({ ...i })), inversion, enabled: true } },
    }));
    controller.notify("info", `Sent ${items.length} chords in ${keyName(key)} to the Studio. Undo with Ctrl+Z.`);
  };

  return (
    <Panel title="Chord progression generator">
      <div className="controls-grid">
        <Select label="Key" value={key.tonic} options={keyTonics(key.mode).map((t) => ({ value: t, label: t }))} onChange={(tonic) => setKey({ ...key, tonic })} />
        <Select label="Mode" value={key.mode} options={[{ value: "major" as Mode, label: "major" }, { value: "minor" as Mode, label: "minor" }]}
          onChange={(mode) => setKey({ tonic: keyTonics(mode)[keyTonics(key.mode).indexOf(key.tonic)]!, mode })} />
        <Select label="Bars" value={opts.length} options={[2, 3, 4, 5, 6, 7, 8].map((n) => ({ value: n, label: String(n) }))} onChange={(n) => set("length", n)} />
        <Select label="Style" value={opts.style} options={(["pop", "worship", "jazz", "classical"] as Style[]).map((s) => ({ value: s, label: s }))} onChange={(s) => set("style", s)} />
        <Select label="Mood" value={opts.mood} options={(["neutral", "bright", "melancholic", "tense"] as Mood[]).map((m) => ({ value: m, label: m }))} onChange={(m) => set("mood", m)} />
        <Select label="Complexity" value={opts.complexity} options={[{ value: 1 as const, label: "Triads" }, { value: 2 as const, label: "7ths on ii and V" }, { value: 3 as const, label: "7ths everywhere" }]} onChange={(c) => set("complexity", c)} />
        <Select label="Ending" value={opts.cadence} options={[{ value: "authentic" as Cadence, label: "V → I (authentic)" }, { value: "plagal" as Cadence, label: "IV → I (plagal)" }, { value: "half" as Cadence, label: "on V (half)" }, { value: "none" as Cadence, label: "free" }]} onChange={(c) => set("cadence", c)} />
        <Toggle pressed={opts.startOnTonic} onChange={(v) => set("startOnTonic", v)}>Start on I</Toggle>
      </div>
      <div className="controls-row">
        <button type="button" className="primary" onClick={generate}>Generate</button>
        <button type="button" onClick={play} disabled={!items}>▶ Play</button>
        <Select label="Inversion" value={inversion} options={[0, 1, 2].map((v) => ({ value: v, label: ["Root position", "1st inversion", "2nd inversion"][v]! }))} onChange={setInversion} />
        <button type="button" onClick={send} disabled={!items}>Send to Studio</button>
        {seed !== null && <span className="hint">seed {seed}</span>}
      </div>
      {described ? (
        <ol className="generated" aria-label="Generated progression">
          {described.map((c, i) => (
            <li key={i}>
              <Select label={`Chord ${i + 1}`} hideLabel value={c.degree} options={options}
                onChange={(degree: Degree) => setItems((its) => its!.map((it, j) => (j === i ? { ...it, degree } : it)))} />
              <Toggle pressed={c.seventh} label={`Seventh, chord ${i + 1}`} onChange={(seventh) => setItems((its) => its!.map((it, j) => (j === i ? { ...it, seventh } : it)))}>7th</Toggle>
              <strong className="chord-symbol">{c.chord.symbol}</strong>
              <span className="reason">{c.reason}</span>
            </li>
          ))}
        </ol>
      ) : <p className="hint">Choose a style and mood, then Generate. Every chord choice is explained from the notes.</p>}
      {cadence && <p className="callout">{cadence}</p>}
      <p className="hint">
        How it works: each next chord is drawn from a published table of common moves (e.g. V → I, ii → V), weighted up by the
        chosen style and towards major or minor chords by mood. No machine learning is involved.
      </p>
    </Panel>
  );
}
